import {gql} from '@apollo/client';

import {ProcedureParameterConfirmationMethod} from 'graphql-client/__generated/graphqlGlobalTypes';
import * as ApolloWrappers from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {accessibleObjs, isInaccessible} from 'graphql-client/InaccessibleInfo';
import makeCustomResolutionResultFromTypedValue from 'notebook/integrations/samples/transfer-table-columns/makeCustomResolutionResultFromTypedValue';
import {
  ProceduresEquipmentConfirmationValidationQuery,
  ProceduresValidateMaterialInputConfirmationMethodAndDataTypeQuery,
  ServerResolutionResult,
} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {isEquipmentValidWithModel} from 'procedures/shared-components/validation/isEquipmentValidWithModel';
import {
  BioentityItemsQuery,
  BioentityItemsQueryVariables,
} from 'procedures/utils/__generated/BioentityItemsQuery';
import {BIOENTITY_ITEMS_QUERY, BioentityForProcedure} from 'procedures/utils/useBioentitiesForProcedures';
import {FieldType, RequiredLinkTagSchema} from 'schemas/fields/SchemaFieldConstants';

import {
  IsSchemaContainableQuery,
  IsSchemaContainableQueryVariables,
} from './__generated/IsSchemaContainableQuery';

export const EQUIPMENT_MODEL_VALIDATION_MISMATCH_ERROR: string = `Equipment's model does not match the model(s) specified`;

// exporting for testing
export const getBioentityItems = async (
  bioentityIds: Array<string>
): Promise<Array<BioentityForProcedure>> => {
  const result = await ApolloWrappers.doQueryWithoutCache<BioentityItemsQuery, BioentityItemsQueryVariables>(
    {query: BIOENTITY_ITEMS_QUERY},
    {variables: {bioentityIds}}
  );
  return accessibleObjs(result.bioentities).map((bioentity) => ({
    id: bioentity.id,
    tags: bioentity.tagsJson,
    tagSchema:
      bioentity.tagSchema != null
        ? {
            id: bioentity.tagSchema.id,
          }
        : null,
  }));
};

// Held in an object so tests can stub the fetch used below.
export const bioentityItemsFetcher = {getBioentityItems};

export async function validateEquipmentConfirmation(
  query: ProceduresEquipmentConfirmationValidationQuery
): Promise<ServerResolutionResult> {
  const {bioentityId, equipmentTypedValue, modelTypedValue} = query;
  const bioentities = await bioentityItemsFetcher.getBioentityItems([bioentityId]);

  if (!isEquipmentValidWithModel(equipmentTypedValue, modelTypedValue, bioentities)) {
    return makeCustomResolutionResultFromTypedValue(
      equipmentTypedValue,
      EQUIPMENT_MODEL_VALIDATION_MISMATCH_ERROR
    );
  }

  return makeCustomResolutionResultFromTypedValue(equipmentTypedValue);
}

const IS_SCHEMA_CONTAINABLE_QUERY = gql`
  query IsSchemaContainableQuery($tagSchemaId: StrictString!) @skipAudit @engComponent(name: PROCEDURES) {
    tagSchema(id: $tagSchemaId) {
      ... on TagSchemaNode {
        id
        isContainable
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

/**
 * This customeval is only used to figure out what validation errors there are. It always returns a null
 * typedvalue - the only useful information is the error in the result.
 */
export async function validateMaterialInputConfirmationMethodAndDataType(
  query: ProceduresValidateMaterialInputConfirmationMethodAndDataTypeQuery
): Promise<ServerResolutionResult> {
  const {confirmationMethod, dataType} = query;
  const passResult = makeCustomResolutionResultFromTypedValue(null);
  if (confirmationMethod !== ProcedureParameterConfirmationMethod.PLATE_MAP) {
    return passResult;
  }

  if (dataType.type !== FieldType.BIOENTITY_LINK) {
    return makeCustomResolutionResultFromTypedValue(
      null,
      'Material input needs to have an entity type to be confirmed via plate maps'
    );
  }

  const requiredTagSchemaId = (dataType.requiredLink as RequiredLinkTagSchema)?.tagSchema?.id;
  if (requiredTagSchemaId == null) {
    return passResult;
  }
  const {tagSchema: requiredTagSchema} = await ApolloWrappers.doQueryWithoutCache<
    IsSchemaContainableQuery,
    IsSchemaContainableQueryVariables
  >({query: IS_SCHEMA_CONTAINABLE_QUERY}, {variables: {tagSchemaId: requiredTagSchemaId}});

  if (!isInaccessible(requiredTagSchema) && !requiredTagSchema.isContainable) {
    return makeCustomResolutionResultFromTypedValue(
      null,
      'Material input entity schema needs to be containable to be confirmed via plate maps'
    );
  }

  return passResult;
}
